import React, { useEffect, useState } from 'react'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { useTenant } from '@emerald-works-nova/auth'
import { useAlert, useGroup, useExternalApp } from '../../hooks'
import { ALL_ADMIN, ALL_ENTERPRISE, CONSUMER } from '../../services/cognito/userGroups'
import { useStyles } from './style'
import {
  Drawer,
  Typography,
  Box,
  IconButton
} from '@mui/material'
import { ActionButton } from '../action-buttons'
import { Link } from 'react-router-dom'
import CloseIcon from '@mui/icons-material/Close'
import { FreeAccountCtaLock } from '../icons'

const MindsetAgent = ({ open, onClose, from }) => {
  const classes = useStyles()
  const { enqueueMsg } = useAlert()
  const tenant = useTenant()
  const { isInGroup } = useGroup()
  const { getMtUrl } = useExternalApp()
  const [authToken, setAuthToken] = useState(null)
  const [appUid, setAppUid] = useState(null)
  const hasMindsetAiAccess = isInGroup([...ALL_ENTERPRISE, ...ALL_ADMIN, CONSUMER]) && tenant.features?.mindsetAi

  const agentUid = process.env.REACT_APP_MINDSET_AGENT_UID
  const tenantId = tenant?.tenantKey ? `${tenant?.tenantKey}.` : ''

  const [askMCoachSignUpClicks] = useEvent([{ eventName: 'askMCoachSignUpClicks' }])

  const [getAuthToken] = useEvent([{
    eventName: 'getAuthToken',
    onSuccess: (payload) => {
      setAuthToken(payload.authToken)
      setAppUid(payload.appUid)
    },
    onError: (error) => {
      enqueueMsg('Unable to retrieve auth token', 'error')
      console.error('Unable to retrieve auth token', error)
    }
  }])

  useEffect(() => {
    if (!authToken && !appUid && hasMindsetAiAccess) {
      getAuthToken.trigger()
    }
  }, [authToken, appUid, getAuthToken, hasMindsetAiAccess])

  useEffect(() => {
    if (authToken && appUid) {
      try {
        window.mindset.initApp({
          appUid,
          containerId: 'agent-div',
          authToken
        })
        window.mindset.startAgentThread({
          agentUid,
          variables: { tenantId }
        })
      } catch (error) {
        console.error('Error loading Mindset Agent:', error)
      }
    } else {
      console.log('Auth token or appUid is not available yet', authToken, appUid)
      console.log('Mindset Agent is already connected')
    }
  }, [authToken, appUid, agentUid, tenantId])

  if (!hasMindsetAiAccess) {
    return (
      <Drawer
        open={open}
        className={classes.root}
        variant='persistent'
        anchor='right'
        data-test='mindset-agent-drawer'
      >
        <Box className={classes.container} data-test='mindset-agent-container'>
          <Box className={classes.close} display='flex' justifyContent='flex-end' data-test='mindset-agent-close-button-container'>
            <IconButton
              aria-label='close'
              onClick={onClose}
              data-test='mindset-agent-close-button'
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Box className={classes.background} />
          <Box className={classes.ctaContainer}>
            <Box className={classes.ctaContent}>
              <FreeAccountCtaLock />
              <Typography className={classes.message} data-test='mindset-ai-cta-message'>
                You&#39;ll need to be a Mindtools Member to access this feature
              </Typography>
              <Box width='100%'>
                <ActionButton
                  variant='primary'
                  label='Join Today'
                  data-test='mindset-ai-cta-create-account'
                  data-gtm='mindset-ai-cta-create-account'
                  href={getMtUrl('join')}
                  className={classes.joinMessage}
                  onClick={() => askMCoachSignUpClicks.trigger({ from })}
                />
              </Box>
              <Typography className={classes.goToSignMessage}>Already have an account? <Link to='/login' data-test='mindset-ai-cta-sign-in'>Sign in</Link></Typography>
            </Box>
          </Box>
        </Box>
      </Drawer>
    )
  }

  return (
    <Drawer
      open={open}
      className={classes.root}
      variant='persistent'
      anchor='right'
      data-test='mindset-agent-drawer'
    >
      <Box className={classes.container} data-test='mindset-agent-container'>
        <Box display='flex' justifyContent='flex-end' data-test='mindset-agent-close-button-container'>
          <IconButton
            aria-label='close'
            onClick={onClose}
            data-test='mindset-agent-close-button'
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant='h5' align='center' gutterBottom data-test='mindset-agent-title'>
          Ask M: Coach
        </Typography>
        <div id='agent-div' className={classes.agent} data-test='mindset-agent-div' />
      </Box>
    </Drawer>
  )
}

export default MindsetAgent
